import {useEffect, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {axiosFunction, toastMessage} from "../../../components/commonFunctions";
import {v2ReactURL} from "../../../utils/constant.ts";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import useResponsive from "../../../hooks/useResponsive.ts";
import ErrorPage from "../../Error/ErrorPage.tsx";
import Loader from "../../../utils/Loader.tsx";
import {Column, Row, Wrapper, WrapperCard, WrapperContent, WrapperSubHeader} from "../../../components/common";
import BreadCrumbComponent from "../../../components/common/BreadCrumbComponent/BreadCrumbComponent.tsx";
import ContactFormAccordian from "../../../components/common/Accordian/ContactFormAccordian.tsx";
import Footer from "../../../layout/footer/Footer.tsx";
import {ErrorState} from "../../Contact/AddContact/AddContact.tsx";
import LoyaltyGeneralDetailsFormComponent
    from "../../../components/common/FormComponent/VLoyalty/LoyaltyGeneralDetailsFormComponent.tsx";
import {
    CampaignForm, campaignSchema
} from "../EditCampaign/EditCampaignSchema.ts";
import LoyaltyParameterDetailsFormComponent
    from "../../../components/common/FormComponent/VLoyalty/LoyaltyParameterDetailsComponent.tsx";
import parameterDetailsIcon from "../../../assets/images/main/parameterDetailsIcon.svg"
import {apiResponseHandler} from "../../../utils/functions/apiResponseHandler.ts";
import {DateFormat} from "../../../utils/DateFormat.ts";
import { useToggleContext } from '../../../layout/ToggleContext.tsx';



const AddCampaign = () => {
    const navigate = useNavigate();
    const isMobile = useResponsive();
const { isToggle } = useToggleContext();
    const [error, setError] = useState<ErrorState | null>(null);
    const [isLoading, setLoading] = useState<boolean>(true);
    const [isLoadingBtn, setIsLoadingBtn] = useState(false);
    const [isPercentage, setIsPercentage] = useState<boolean>(false);
    const [selectedCampaignType] = useState({
        value: "Discount", label: "Discount"
    });
    const [selectedRedemptionType] = useState({
        value: "Multiple", label: "Multiple"
    });

    const [showDiscountField, setShowDiscountField] = useState<boolean>(true);


    useEffect(() => {
        const fetchNewCampaignDetail = async () => {
            setIsLoadingBtn(true);
            const response = await axiosFunction(
                `${v2ReactURL}/loyalty/campaign/new`,
                "GET"
            );

            const error: ErrorState | null = apiResponseHandler(response, false);
            if (error != null) {
                setError(error);
                setIsLoadingBtn(false);
                return;
            }

            setLoading(false);
            setIsLoadingBtn(false);
            setError(null);
        };
        fetchNewCampaignDetail();
    }, []);

    const {
        register,
        handleSubmit,
        resetField,
        formState: {errors},
        setValue,
        control,
        watch
    } = useForm<CampaignForm>({
        defaultValues: {
            name: null,
            points: null,
            discount: null,
            type: selectedCampaignType,
            description: null,
            redemptionType: selectedRedemptionType,
            launchDate: new Date().toISOString().split("T")[0],
            expiryDate: null,
            minPurchaseAmount: 0,
            isPercentage: false
        },
        resolver: yupResolver(campaignSchema as any),
        mode: "onChange",
    });

    const payloadData = (data: CampaignForm) => {
        return {
            loyaltyCampaignId: 0,
            name: data.name,
            type: data.type.value,
            points: data.points,
            description: data.description,
            discount: data.type.value === "Discount" ? data.discount : 0,
            redemptionType: data.redemptionType.value,
            minPurchaseAmount: data.minPurchaseAmount,
            launchDate: data.launchDate && DateFormat(data.launchDate),
            expiryDate: data.expiryDate && DateFormat(data.expiryDate),
            discountInPercentage : isPercentage
        };
    };

    const saveCampaign = async (data: CampaignForm) => {
        if (data.launchDate && data.expiryDate) {
            const launchDate = new Date(data.launchDate);
            const expiryDate = new Date(data.expiryDate);

            if (expiryDate < launchDate) {
                toastMessage("Expiry date should be after the launch date.");
                return;
            }
        }

        const response = await axiosFunction(
            `${v2ReactURL}/loyalty/campaign/save`,
            "POST",
            payloadData(data)
        );

        const error: ErrorState | null = apiResponseHandler(response, true);
        if (error != null) {
            toastMessage(error.message);
            return;
        }
        return response;
    }

    const onSave = async (data: CampaignForm) => {
        const response = await saveCampaign(data)
        if (response) {
            navigate(`/loyalty/campaign/${response.data.response}`);
        }
    };

    const onSaveNew = async (data: CampaignForm) => {
        const response = await saveCampaign(data)
        if (response) {
            toastMessage("Campaign created successfully", "success");
            resetFormFields();
        }
    };

    const resetFormFields = () => {
        resetField("type");
        resetField("redemptionType");
        resetField("minPurchaseAmount");
        resetField("expiryDate");
        resetField("launchDate");
        resetField("description");
        resetField("discount");
        resetField("points");
        resetField("name");
        setShowDiscountField(true);
    };

    const handleClear = () => {
        resetFormFields();
    };

    if (error) {
        return <ErrorPage error={error}/>;
    }

    if (isLoading) {
        return <Loader/>;
    }

    return <Wrapper
        navUrl=""
        customStyle={{height: "auto"}}
        subClassName={"addContactWrapper"}
    >
        <WrapperSubHeader>
            <Column md={8}></Column>
            <Column md={2}></Column>
            <Column md={2} className="pl-md-0 mt-1 mt-md-0"></Column>
        </WrapperSubHeader>
        <WrapperContent>
            <BreadCrumbComponent
                type={`Campaign`}
                subTypeClickable={false}
                typeLink="/loyalty"
            />
            <Row>
                <WrapperCard>
                    <ContactFormAccordian
                        generalDetailsForm={
                            <LoyaltyGeneralDetailsFormComponent
                                isPercentage={isPercentage}
                                setIsPercentage={setIsPercentage}
                                register={register}
                                errors={errors}
                                control={control}
                                setValue={setValue}
                                watch={watch}
                                showDiscountField={showDiscountField}
                                setShowDiscountField={setShowDiscountField}
                            />
                        }
                        addressDetailsForm={
                            <LoyaltyParameterDetailsFormComponent
                                register={register}
                                errors={errors}
                                control={control}
                                setValue={setValue}
                                watch={watch}
                                showDiscountField={showDiscountField}
                                setShowDiscountField={setShowDiscountField}
                            />
                        }
                        secondCollapseTitle="Parameter Details"
                        secondCollapseIcon={parameterDetailsIcon}
                    />
                </WrapperCard>
            </Row>
        </WrapperContent>
        <div
            style={{
                position: "fixed",
                bottom: 0,
                width: `calc(100% - ${isToggle ? 78 : 230}px)`,
                zIndex: 999,
                left: isMobile ? 0 : isToggle ? 78 : 230
            }}
        >
            <Footer
                onSubmit={handleSubmit(onSave)}
                onCreateNewSave={handleSubmit(onSaveNew)}
                handleClear={handleClear}
                clearBtn={true}
                addressDetailsError={false}
                onCancelClick={() => {
                    navigate("/loyalty");
                }}
                isLoading={isLoadingBtn}
            />
        </div>
    </Wrapper>
};

export default AddCampaign;